import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Panel } from "@/components/common";
import { AnnouncementDialog } from "@/components/AnnouncementForm";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDateTime } from "@/lib/api";
import { can } from "@/lib/perm";

export default function ComunicazioneDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = can(user, "publish_announcements");
  const [a, setA] = useState(null);
  const [edit, setEdit] = useState(false);
  const load = useCallback(() => api.get(`/announcements/${id}`).then((r) => setA(r.data)).catch(() => nav("/comunicazioni")), [id, nav]);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    await api.delete(`/announcements/${id}`);
    toast.success("Comunicazione eliminata");
    nav("/comunicazioni");
  };

  if (!a) return null;
  const readers = a.recipients?.filter((r) => r.read_at).length;
  return (
    <div data-testid="announcement-detail-page">
      <Link to="/comunicazioni" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-announcements"><ArrowLeft className="h-4 w-4" />Comunicazioni</Link>
      <div className="grid gap-6 lg:grid-cols-3">
        <article className="fade-up rounded-lg border bg-card p-6 sm:p-8 lg:col-span-2">
          <p className="text-xs text-muted-foreground">{a.author_name} · {fmtDateTime(a.created_at)}{a.updated_at && ` · modificata ${fmtDateTime(a.updated_at)}`}</p>
          <h1 className="mt-2 text-2xl font-bold sm:text-3xl" data-testid="announcement-title">{a.title}</h1>
          <div className="mt-6 whitespace-pre-wrap text-[15px] leading-relaxed" data-testid="announcement-body">{a.body}</div>
          {isAdmin && (user.role === "admin" || a.author_id === user.id) && (
            <div className="mt-8 flex gap-2 border-t pt-5">
              <Button variant="outline" onClick={() => setEdit(true)} data-testid="announcement-edit-button"><Pencil className="mr-1.5 h-4 w-4" />Modifica</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="outline" className="text-destructive" data-testid="announcement-delete-button"><Trash2 className="mr-1.5 h-4 w-4" />Elimina</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Eliminare la comunicazione?</AlertDialogTitle><AlertDialogDescription>L'operazione non è reversibile.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={remove} data-testid="announcement-delete-confirm">Elimina</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </article>
        {isAdmin && a.recipients && (
          <Panel title={`Letture ${readers}/${a.recipients.length}`} testId="announcement-read-tracking">
            <ul className="max-h-[480px] divide-y overflow-y-auto">
              {a.recipients.map((r) => (
                <li key={r.id} className="flex items-center gap-2.5 px-5 py-2.5 text-sm">
                  {r.read_at ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <Circle className="h-4 w-4 text-muted-foreground/50" />}
                  <span className="flex-1">{r.name}</span>
                  <span className="text-xs text-muted-foreground">{r.read_at ? fmtDateTime(r.read_at) : "Non letta"}</span>
                </li>
              ))}
            </ul>
          </Panel>
        )}
      </div>
      {edit && <AnnouncementDialog open={edit} onOpenChange={setEdit} initial={a} onSaved={load} />}
    </div>
  );
}
